import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const ToasterCtx = createContext(null);
const FONT_STACK = "'Orbitron','Noto Sans TC','微軟正黑體',sans-serif";

function ToastItem({ t, index, now, onClose }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const remainingMs = t.expiresAt != null ? Math.max(0, t.expiresAt - now) : null;
  const remainingSec = remainingMs != null ? Math.ceil(remainingMs / 1000) : null;
  const percentLeft =
    remainingMs != null && t.autoHideMs > 0
      ? Math.max(0, Math.min(100, (remainingMs / t.autoHideMs) * 100))
      : null;

  const delay = `${index * 40}ms`;

  return (
    <div
      className="toast show mb-2 border-0 shadow-sm"
      role="alert"
      style={{
        transform: show ? "translateY(0)" : "translateY(-14px)",
        opacity: show ? 1 : 0,
        transition: `transform .28s cubic-bezier(.2,.8,.2,1) ${delay}, opacity .28s ease ${delay}`,
      }}
    >
      <div className={`toast-header ${bgByVariant(t.variant)} text-white`}>
        <i className={`${iconByVariant(t.variant)} me-2`} />
        <strong className="me-auto">{t.title}</strong>
        <button
          type="button"
          className="btn-close btn-close-white ms-2 mb-1"
          onClick={onClose}
          aria-label="Close"
        />
      </div>

      {(t.message || t.autoHideMs >= 0) && (
        <div className="toast-body">
          {t.message && <div className="mb-1">{t.message}</div>}

          {t.autoHideMs > 0 ? (
            <>
              <small className="text-muted d-block mb-1">
                將在 <strong>{remainingSec}</strong> 秒後自動關閉
              </small>
              <div
                className="progress"
                role="progressbar"
                aria-label="自動關閉倒數"
                aria-valuenow={Math.round(percentLeft ?? 0)}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{ height: 4 }}
              >
                <div
                  className={`progress-bar ${barByVariant(t.variant)}`}
                  style={{
                    width: `${percentLeft ?? 0}%`,
                    transition: "width .5s linear",
                  }}
                />
              </div>
            </>
          ) : (
            <small className="text-muted">不會自動關閉</small>
          )}
        </div>
      )}
    </div>
  );
}

export function ToasterProvider({ children, defaultAutoHideMs = 5000, max = 5 }) {
  const [items, setItems] = useState([]);
  const [now, setNow] = useState(Date.now());

  const hasCountdown = useMemo(
    () => items.some((item) => item.expiresAt != null),
    [items]
  );

  useEffect(() => {
    // 沒有正在倒數的 toast 時不要讓最外層 Provider 永久每 500ms setState。
    // PublicChat 也包在這個 Provider 裡，空閒時應維持完全靜止。
    if (!hasCountdown) return undefined;

    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [hasCountdown]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (item) => {
      const id = crypto.randomUUID();
      const autoHideMs = item.autoHideMs ?? defaultAutoHideMs;
      const createdAt = Date.now();
      const toast = {
        id,
        title: item.title ?? "通知",
        message: item.message ?? "",
        variant: item.variant ?? "danger", // 'danger' | 'warning' | 'info' | 'success'
        autoHideMs,
        createdAt,
        expiresAt: autoHideMs > 0 ? createdAt + autoHideMs : null,
      };

      setItems((prev) => [toast, ...prev].slice(0, max));

      if (autoHideMs > 0) {
        setTimeout(() => remove(id), autoHideMs);
      }
    },
    [defaultAutoHideMs, max, remove]
  );

  const value = useMemo(() => ({ push, remove }), [push, remove]);

  return (
    <ToasterCtx.Provider value={value}>
      {children}

      <div
        className="position-fixed end-0 p-3"
        style={{
          zIndex: 9001,
          width: 360,
          top: "calc(var(--nav-height, 56px) + 2px)",
          fontFamily: FONT_STACK,
          pointerEvents: "none",
        }}
        aria-live="polite"
        aria-atomic="true"
      >
        {items.map((t, i) => (
          <ToastItem
            key={t.id}
            t={t}
            index={i}
            now={now}
            onClose={() => remove(t.id)}
          />
        ))}
      </div>
    </ToasterCtx.Provider>
  );
}

export function useToaster() {
  const ctx = useContext(ToasterCtx);
  if (!ctx) throw new Error("useToaster must be used within <ToasterProvider>");
  return ctx;
}

function bgByVariant(v) {
  switch (v) {
    case "danger": return "bg-danger";
    case "warning": return "bg-warning";
    case "success": return "bg-success";
    default: return "bg-primary"; // info
  }
}
function barByVariant(v) {
  switch (v) {
    case "danger": return "bg-danger";
    case "warning": return "bg-warning";
    case "success": return "bg-success";
    default: return "bg-primary";
  }
}
function iconByVariant(v) {
  switch (v) {
    case "danger": return "bi bi-x-octagon-fill";
    case "warning": return "bi bi-exclamation-triangle-fill";
    case "success": return "bi bi-check-circle-fill";
    default: return "bi bi-info-circle-fill";
  }
}
